// Section id the drawer is rendered under. Must match the `cart-drawer` entry in
// cart-drawer.js getSectionsToRender — that is the key publishers put their rendered
// drawer HTML under in the cartUpdate payload, and the id the fallback read asks for.
const CART_DRAWER_SECTION_ID = 'cart-drawer';

// Latest issued /cart/change request per cart line, keyed by `item.key` when the caller has
// one, falling back to `variantId:line` or the line number alone for callers that pass
// neither (cart-remove-button, legacy call sites). See updateQuantity for why.
//
// Module scope, not instance state, is load-bearing: CartDrawerItems renders the drawer under
// `selector: '.drawer__inner'`, and cart-drawer.liquid nests <cart-drawer-items> inside that
// element — so applySectionUpdate's `innerHTML =` destroys and recreates this element on every
// response. A token held on `this` would be wiped by the first response to arrive and the next
// stale one would be applied by a fresh instance starting from zero.
const cartQuantityRequestTokens = new Map();

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    this.cartDrawer = document.querySelector('cart-drawer');
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      event.target.dataset.lineKey
    );
  }

  onCartUpdate(event) {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      /*
       * Every publisher of cartUpdate already asked Shopify to re-render the drawer
       * alongside its own mutation (`sections` rides on the write, see
       * cart-drawer.js getSectionsToRender), so the HTML this element needs is already in
       * the event. Re-fetching it cost a second round trip on the critical path of every
       * add-to-cart — 314 ms in the IDS-7204 measurements — for markup we were handed.
       *
       * The fetch stays as the fallback: a publisher that sends no sections at all
       * (vet-free-gift-popup:309) or one whose payload predates a section rename must keep
       * repainting the drawer exactly as before.
       */
      // Set only by publishers that already repainted #CartDrawer themselves. Repeating it here
      // would rewrite the same node with identical markup, re-parsing the drawer section and
      // remounting every child Web Component on the add-to-cart critical path.
      if (event.drawerRendered) return;

      const publishedSection = event?.cartData?.sections?.[CART_DRAWER_SECTION_ID];
      if (typeof publishedSection === 'string' && publishedSection.trim() !== '') {
        this.renderCartDrawerSection(publishedSection);
        return;
      }

      window
        .cartFetchWithRetry(`${routes.cart_url}?section_id=${CART_DRAWER_SECTION_ID}`)
        .then((response) => response.text())
        .then((responseText) => {
          this.renderCartDrawerSection(responseText);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  /**
   * Repaints #CartDrawer from a rendered `cart-drawer` section.
   *
   * Takes the same markup either way: the Section Rendering API returns the identical
   * `.shopify-section` wrapper whether it arrives on a publisher's mutation response or
   * from a standalone `?section_id=` read, so one parser serves both paths.
   */
  renderCartDrawerSection(sectionHtml) {
    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const section = html.querySelector('.shopify-section');
    const sourceCartDrawer = section && section.querySelector('#CartDrawer');
    const targetCartDrawer = document.querySelector('#CartDrawer');
    if (!sourceCartDrawer || !targetCartDrawer) return;

    targetCartDrawer.innerHTML = sourceCartDrawer.innerHTML;
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return;

    const hasEmptyState = targetCartDrawer.querySelector('.drawer__inner-empty');
    cartDrawerEl.classList.toggle('is-empty', !!hasEmptyState);
    const overlay = cartDrawerEl.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', () => cartDrawerEl.close());
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, lineKey) {
    this.enableLoading(line);

    // Keyed by `item.key` (`data-line-key`) when the caller has one: it is the only
    // identifier that is unique per cart line *and* stable across renumbering.
    //
    // `line` alone is unsafe: /cart/change renumbers lines, and the free-gift engine
    // adds/removes its own line in parallel, so a line-keyed token can end up guarding a
    // different product than the one the customer clicked.
    //
    // `variantId` alone is not unique either: it carries only `item.variant.id`, with no
    // selling-plan discriminator, so a one-time purchase and a subscription of the same
    // product are two lines sharing one variant id — the common case in this store. With a
    // variant-only key, updating both within a round trip made each line's token overwrite
    // the other's, and the newest response for one line was then misjudged as stale and its
    // DOM update dropped.
    //
    // The `variantId:line` composite fixed that collision but not renumbering: a concurrent
    // write that shifts a line makes the same logical line hash to a new key, so the token
    // lookup misses. `item.key` survives the shift, so the newest request for a line is
    // still recognised as newest after the gift engine has inserted or dropped its row.
    // It stays the fallback for callers that pass no key (cart-remove-button, legacy), where
    // a miss is the safe failure: the request counts as current and its response is applied
    // rather than wrongly discarded.
    const requestTokenKey = lineKey ?? (variantId == null ? `${line}` : `${variantId}:${line}`);
    const requestToken = (cartQuantityRequestTokens.get(requestTokenKey) ?? 0) + 1;
    cartQuantityRequestTokens.set(requestTokenKey, requestToken);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    window
      .cartFetchWithRetry(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        // A newer request for this line was issued while this one was in flight. POST
        // /cart/change takes ~1s, so two clicks at a human pace overlap and each response
        // repainted the drawer from its own already-outdated cart — the number visibly
        // snapped back, and the older response's clamp check fired a false
        // "You can only add N of this item" error. Only the newest request may touch the
        // DOM; older ones stop here.
        //
        // The event still publishes: this response is a complete, truthful cart, and the
        // free-gift engine is purely event-driven (no observers, no drawer-open hook), so
        // dropping a cartUpdate would leave the gift unrecalculated even though a quantity
        // bump can cross the $60 threshold. syncGift recomputes the whole desired state from
        // the full cart and already coalesces overlapping events, so the event count seen by
        // every subscriber is unchanged from before this fix.
        if (cartQuantityRequestTokens.get(requestTokenKey) !== requestToken) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
          return;
        }

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        const isCartEmpty = parsedState.item_count === 0;

        if (parsedState.errors) {
          // The row can be gone: the free-gift engine removes its line in parallel, and
          // /cart/change.js renumbers every line after it, so the id built from the
          // pre-request line number may no longer resolve.
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', isCartEmpty);
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', isCartEmpty);
        if (this.cartDrawer) this.cartDrawer.classList.toggle('is-empty', isCartEmpty);

        this.getSectionsToRender().forEach((section) => {
          const sectionRoot = document.getElementById(section.id);
          if (!sectionRoot) return;
          const elementToReplace = sectionRoot.querySelector(section.selector) || sectionRoot;
          this.applySectionUpdate(section, elementToReplace, parsedState.sections[section.section]);
        });
        const updatedLine = parsedState.items[line - 1];
        const updatedValue = updatedLine ? updatedLine.quantity : undefined;
        let message = '';
        // The guards all cover the same case: a concurrent write — the free-gift engine
        // adding or removing its line — renumbered the cart between this request and its
        // response. The line number is then stale, so the element lookup can miss and
        // items[line - 1] can point past the end or at a different product. None of that
        // means the customer's change failed, and reporting an error for it is what
        // surfaced "There was an error while updating your cart" on a cart that had just
        // updated correctly. A genuine quantity clamp still reports, because that resolves
        // the line and returns a number.
        //
        // The identity check is what the length check alone cannot do. When the gift line is
        // added *before* the customer's clicks, both carts have the same length while every
        // line after the gift has shifted by one — so items[line - 1] resolves to a real but
        // wrong line, and comparing its quantity to the input produced a false "You can only
        // add N of this item" on a cart that was correct.
        //
        // Identity is compared on `item.key`, not `variant_id`: variant id is not unique per
        // line. A subscription and a one-time purchase of the same product are two lines
        // sharing one variant id, so after a shift between those two lines a variant-id
        // comparison passes on the wrong row and the false clamp error returns — the very bug
        // this guard exists to prevent. `item.key` carries the selling-plan discriminator and
        // is present on every /cart/change line.
        // Callers that pass no lineKey (cart-remove-button, legacy) keep the old behaviour.
        const isSameLine = !!updatedLine && (lineKey == null || updatedLine.key === lineKey);
        if (
          quantityElement &&
          isSameLine &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue !== 'undefined') {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          this.cartDrawer
            ? trapFocus(this.cartDrawer, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (isCartEmpty && this.cartDrawer) {
          trapFocus(this.cartDrawer.querySelector('.drawer__inner-empty'), this.cartDrawer.querySelector('a'));
        } else if (document.querySelector('.cart-item') && this.cartDrawer) {
          trapFocus(this.cartDrawer, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  applySectionUpdate(section, elementToReplace, sectionHtml) {
    const isCartIconBubble = section.id === 'cart-icon-bubble' || section.section === 'cart-icon-bubble';
    // <cart-drawer> is upgraded after this element caches it in the constructor, and
    // #CartDrawer innerHTML is replaced on every update — so the cached reference can be
    // null or stale. Re-query at call time so the VET branch isn't skipped.
    const cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
    if (
      isCartIconBubble &&
      elementToReplace.closest('.header-vet') &&
      cartDrawer &&
      typeof cartDrawer.renderCartIconBubbleForVetHeader === 'function'
    ) {
      cartDrawer.renderCartIconBubbleForVetHeader(elementToReplace, sectionHtml);
      return;
    }
    // sections/cart-icon-bubble.liquid renders a bare fragment with no `.shopify-section`
    // wrapper, so the selector legitimately misses here. Leave the existing DOM in place
    // rather than letting a cosmetic miss throw and surface a cart error to the customer.
    const updatedHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    if (updatedHTML === null) return;
    elementToReplace.innerHTML = updatedHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            window.cartFetchWithRetry(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
